import BaseModal from 'components/Modals/BaseModal';
import { FEMA_STATUS_CHANNEL_LABELS } from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaStatusCodes';
import { Badge, Button } from 'react-bootstrap';
import type { FemaEnrichedAdvisory } from 'types/fema.types';
import type { IpawsSubmissionStatusMessage } from '../../../../hooks/useIpawsSubmissionFlow';
import type { IpawsPreviewModel } from '../../../../utils/ipawsSubmission.utils';
import IpawsBackendFindingsList from '../IpawsBackendFindingsList/IpawsBackendFindingsList';

type IpawsSubmissionResultModalProps = {
  show: boolean;
  onClose: () => void;
  resultMessage: IpawsSubmissionStatusMessage;
  destinationDetails?: IpawsPreviewModel;
};

/** Fail (red) for an error code, success (green) for a dissemination or validation Ack, else a note (yellow). */
const getAdvisoryVariant = (advisory: FemaEnrichedAdvisory): 'danger' | 'success' | 'warning' => {
  if (advisory.isError) return 'danger';
  if (advisory.disseminated === true || advisory.validationSuccess) return 'success';
  return 'warning';
};

/**
 * Displays the IPAWS submission result returned by the backend POST route: a FEMA outcome with
 * every status item FEMA returned (not just the lead one), the backend's field findings on a
 * refusal, or — while the send gate is closed — the identity of the signed document that stopped
 * at the gate (identifier, digest, signer, the readable XML).
 * @param props The submission result modal props.
 * @returns The rendered submission result modal.
 */
const IpawsSubmissionResultModal = ({
  show,
  onClose,
  resultMessage,
  destinationDetails,
}: IpawsSubmissionResultModalProps): JSX.Element => (
  <BaseModal
    show={show}
    handleShow={onClose}
    backdrop="static"
    keyboard={false}
    closeButton={false}
    title="IPAWS Submission Results"
    size={resultMessage.gate ? 'lg' : 'md'}
    borderWidth="2px"
    footer={
      <Button type="button" variant="primary" onClick={onClose}>
        Close
      </Button>
    }
  >
    <div>
      <div className="d-block mb-0 fw-bold">
        {resultMessage.acceptanceMessage || 'No IPAWS submission result was returned.'}
      </div>
      {destinationDetails ? (
        <section className="border rounded p-2 mt-2" aria-label="FEMA IPAWS destination details">
          <div>
            <strong>{destinationDetails.destinationName}</strong>
          </div>
          <div>
            <strong>Environment:</strong> {destinationDetails.environmentLabel}
          </div>
          <div>
            <strong>COG:</strong> {destinationDetails.cogDisplayId}
          </div>
          <div>
            <strong>Issuing Authority:</strong>{' '}
            {destinationDetails.issuingAuthority || 'Not provided'}
          </div>
          <div>
            <strong>Credential Status:</strong> {destinationDetails.credentialStatus}
          </div>
          {destinationDetails.dissemination ? (
            <p className="mb-0 mt-1 small">{destinationDetails.dissemination}</p>
          ) : null}
          <details className="mt-1">
            <summary>Advanced FEMA COG details</summary>
            <div>FEMA COG ID: {destinationDetails.cogId || 'Not provided'}</div>
            <div>FEMA COG Name: {destinationDetails.cogName || 'Not provided'}</div>
          </details>
        </section>
      ) : null}
      {resultMessage.text ? <div className="d-block mt-2">{resultMessage.text}</div> : null}
      {resultMessage.verificationUrl ? (
        <p className="mb-0 mt-2">
          <a
            className="text-white"
            href={resultMessage.verificationUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View in the FEMA Message Viewer
          </a>
          {resultMessage.cogId ? ` (COG ${resultMessage.cogId})` : ''}
        </p>
      ) : null}
      {resultMessage.advisories?.length ? (
        <div className="mt-2 d-flex flex-column gap-2">
          {resultMessage.advisories.map((advisory) => {
            const variant = getAdvisoryVariant(advisory);

            return (
              <div
                key={`${advisory.channel}-${advisory.code}`}
                className="border rounded rounded-1 p-2"
                style={{
                  backgroundColor: `color-mix(in srgb, var(--color-status-${variant}) 80%, black)`,
                  borderColor: `var(--color-status-${variant})`,
                }}
              >
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <Badge bg={variant}>{advisory.code}</Badge>
                  <span className="fw-semibold">
                    {FEMA_STATUS_CHANNEL_LABELS[advisory.channel]}
                  </span>
                  {advisory.disseminated === false ? (
                    <Badge bg="secondary">Not disseminated</Badge>
                  ) : null}
                </div>
                <p className="mb-0 mt-1 small">{advisory.backendMessage || advisory.remark}</p>
              </div>
            );
          })}
        </div>
      ) : null}
      <IpawsBackendFindingsList findings={resultMessage.findings} />
      {resultMessage.gate ? (
        <div className="mt-3">
          <dl className="row mb-2 small">
            <dt className="col-sm-3">CAP identifier</dt>
            <dd className="col-sm-9 user-select-all">{resultMessage.gate.capIdentifier}</dd>
            <dt className="col-sm-3">Digest</dt>
            <dd className="col-sm-9 user-select-all text-break">
              {resultMessage.gate.digestValue}
            </dd>
            <dt className="col-sm-3">Signed by</dt>
            <dd className="col-sm-9 text-break">{resultMessage.gate.subjectName}</dd>
          </dl>
          <pre
            className="small mb-0 p-2 bg-light border rounded"
            style={{ maxHeight: '20rem', overflow: 'auto', whiteSpace: 'pre' }}
            aria-label="Signed CAP XML, formatted for reading"
          >
            {resultMessage.gate.signedXmlFormatted}
          </pre>
        </div>
      ) : null}
    </div>
  </BaseModal>
);

export default IpawsSubmissionResultModal;
